import React, {useState, useEffect} from "react";
import {Link} from "react-router-dom";
import {ArrowRight, BookOpen} from "lucide-react";

interface CurrentProject {
  id: number;
  title: string;
  blurb: string;
  image_url: string;
  display_order: number;
}

const CurrentProjects: React.FC = () => {
  const [projects, setProjects] = useState<CurrentProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch("/api/current-projects");
      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }
      const data = await response.json();
      setProjects(data.data || []);
    } catch (error) {
      console.error("Error fetching current projects:", error);
    } finally {
      setLoading(false);
    }
  };

  // Determine grid columns based on number of projects
  const getGridCols = (count: number) => {
    if (count === 0) return "md:col-span-2";
    if (count === 1) return "md:col-span-2";
    if (count === 2) return "md:grid-cols-2";
    if (count === 3) return "md:grid-cols-3";
    if (count === 4) return "md:grid-cols-2";
    if (count === 5) return "md:grid-cols-3";
    if (count === 6) return "md:grid-cols-3";
    if (count === 7) return "md:grid-cols-4";
    return "md:grid-cols-4"; // 8 items
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 items-start">
            <div className="bg-primary-300 border-4 border-primary-600 px-8 py-16 shadow-lg">
              <h2 className="text-4xl font-bold text-primary-400 mb-6">
                Current Projects
              </h2>
              <p className="text-lg text-primary-800 mb-8 leading-relaxed">
                Upcoming works from Hallie Parker
              </p>
              <div className="text-center">
                <Link
                  to="/about"
                  className="btn inline-flex items-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                >
                  About the Author
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="md:col-span-2">
              <p className="text-primary-800">Loading projects...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-12 items-start">
          {/* Left side - Current Projects box */}
          <div className="bg-primary-300 border-4 border-primary-600 px-8 py-16 shadow-lg">
            <h2 className="text-4xl font-bold text-primary-400 mb-6">
              Current Projects
            </h2>
            <p className="text-lg text-primary-800 mb-8 leading-relaxed">
              Upcoming works from Hallie Parker
            </p>
            <div className="text-center">
              <Link
                to="/about"
                className="btn inline-flex items-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
              >
                About the Author
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Right side - Project cards */}
          {projects.length === 0 ? (
            <div className="md:col-span-2 grid md:grid-cols-2 gap-8 items-center">
              {/* Placeholder when no projects */}
              <div className="text-center">
                <div className="w-28 h-28 mx-auto mb-3 bg-gray-200 border-4 border-primary-600 rounded-full overflow-hidden shadow-lg">
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <BookOpen className="h-8 w-8" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-primary-400 mb-2">
                  Coming Soon
                </h3>
                <p className="text-xs text-primary-800 leading-relaxed">
                  New projects coming soon!
                </p>
              </div>
            </div>
          ) : (
            <div
              className={`md:col-span-2 grid ${getGridCols(
                projects.length
              )} gap-8 items-center`}
            >
              {projects.map((project) => (
                <div key={project.id} className="text-center">
                  <div className="w-28 h-28 mx-auto mb-3 bg-gray-200 border-4 border-primary-600 rounded-full overflow-hidden shadow-lg">
                    {project.image_url ? (
                      <img
                        src={project.image_url}
                        alt={project.title}
                        width={112}
                        height={112}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <BookOpen className="h-8 w-8" />
                      </div>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-primary-400 mb-2">
                    {project.title}
                  </h3>
                  <p className="text-xs text-primary-800 leading-relaxed">
                    {project.blurb}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CurrentProjects;



